import { db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

// Constants for plan limits
export const PLAN_LIMITS = {
  standard: {
    maxStorage: 1024 * 1024 * 1024, // 1GB in bytes
    maxAccounts: 1,
  },
  pro: {
    maxStorage: 5 * 1024 * 1024 * 1024, // 5GB in bytes
    maxAccounts: 20,
  },
};

// Calculate storage for a single trade
const calculateTradeStorage = (trade) => {
  // Convert trade object to string to estimate storage
  const tradeString = JSON.stringify(trade);
  return new Blob([tradeString]).size;
};

// Calculate storage for a journal entry
const calculateJournalStorage = (journalEntry) => {
  const journalString = JSON.stringify(journalEntry);
  return new Blob([journalString]).size;
};

// Calculate total storage used by a user
export const calculateUserStorage = async (userId) => {
  try {
    // Get all user's trades
    const tradesRef = collection(db, "trades");
    const tradesQuery = query(tradesRef, where("userId", "==", userId));
    const tradesSnapshot = await getDocs(tradesQuery);

    // Get all user's journal entries
    const journalRef = collection(db, "journalEntries");
    const journalQuery = query(journalRef, where("userId", "==", userId));
    const journalSnapshot = await getDocs(journalQuery);

    // Calculate total storage
    let totalTradeStorage = 0;
    let totalJournalStorage = 0;

    tradesSnapshot.forEach((doc) => {
      totalTradeStorage += calculateTradeStorage(doc.data());
    });

    journalSnapshot.forEach((doc) => {
      totalJournalStorage += calculateJournalStorage(doc.data());
    });

    const totalStorage = totalTradeStorage + totalJournalStorage;

    // Update storage usage record
    const storageRef = doc(db, "storageUsage", userId);
    await setDoc(
      storageRef,
      {
        userId,
        totalStorageUsed: totalStorage,
        breakdown: {
          trades: totalTradeStorage,
          journals: totalJournalStorage,
          attachments: 0, // For future use
        },
        lastCalculated: serverTimestamp(),
      },
      { merge: true }
    );

    return totalStorage;
  } catch (error) {
    console.error("Error calculating storage:", error);
    throw error;
  }
};

// Check if user has exceeded storage limit
export const checkStorageLimit = async (userId) => {
  try {
    // Get user's plan
    const planRef = doc(db, "userPlans", userId);
    const planDoc = await getDoc(planRef);

    if (!planDoc.exists()) {
      throw new Error("No plan found for user");
    }

    const plan = planDoc.data();
    const maxStorage = PLAN_LIMITS[plan.planType].maxStorage;

    // Get current storage usage
    const storageRef = doc(db, "storageUsage", userId);
    const storageDoc = await getDoc(storageRef);

    if (!storageDoc.exists()) {
      // Calculate if no storage record exists
      const currentStorage = await calculateUserStorage(userId);
      return {
        hasExceededLimit: currentStorage > maxStorage,
        currentStorage,
        maxStorage,
        remainingStorage: maxStorage - currentStorage,
      };
    }

    const currentStorage = storageDoc.data().totalStorageUsed;

    return {
      hasExceededLimit: currentStorage > maxStorage,
      currentStorage,
      maxStorage,
      remainingStorage: maxStorage - currentStorage,
    };
  } catch (error) {
    console.error("Error checking storage limit:", error);
    throw error;
  }
};

// Format storage size for display
export const formatStorageSize = (bytes) => {
  const sizes = ["Bytes", "KB", "MB", "GB"];
  if (bytes === 0) return "0 Byte";
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
};

// Initialize user plan
export const initializeUserPlan = async (userId, planType = "standard") => {
  try {
    const planRef = doc(db, "userPlans", userId);
    await setDoc(planRef, {
      userId,
      planType,
      startDate: serverTimestamp(),
      endDate: null, // For future use with subscriptions
      maxStorage: PLAN_LIMITS[planType].maxStorage,
      maxAccounts: PLAN_LIMITS[planType].maxAccounts,
      isActive: true,
      autoRenew: false,
      lastUpdated: serverTimestamp(),
    });

    // Initialize storage tracking
    await calculateUserStorage(userId);
  } catch (error) {
    console.error("Error initializing user plan:", error);
    throw error;
  }
};

// Check if user can create new account
export const canCreateNewAccount = async (userId) => {
  try {
    // Get user's plan
    const planRef = doc(db, "userPlans", userId);
    const planDoc = await getDoc(planRef);

    if (!planDoc.exists()) {
      throw new Error("No plan found for user");
    }

    const plan = planDoc.data();

    // Get current number of accounts
    const accountsRef = collection(db, "accountProfiles");
    const accountsQuery = query(accountsRef, where("userId", "==", userId));
    const accountsSnapshot = await getDocs(accountsQuery);

    return {
      canCreate: accountsSnapshot.size < plan.maxAccounts,
      currentAccounts: accountsSnapshot.size,
      maxAccounts: plan.maxAccounts,
    };
  } catch (error) {
    console.error("Error checking account limit:", error);
    throw error;
  }
};
